import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import * as XLSX from 'xlsx';
import { Table, Button, Input, Select, Space, message, Tag, Row, Col, DatePicker, Typography } from 'antd';
import { DownloadOutlined, UploadOutlined, SearchOutlined } from '@ant-design/icons';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const TASK_STATUS_OPTIONS = [
    { value: 'new', label: 'Новая' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'filled', label: 'Заполнена' },
    { value: 'rework', label: 'На доработке' },
    { value: 'approval', label: 'Согласование' },
    { value: 'analysis', label: 'Требует анализа' },
    { value: 'approved', label: 'Утверждено' }
];

const InvoicesPage = () => {
    const [groupedData, setGroupedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [filters, setFilters] = useState({
        search: '',
        status: null,
        dateRange: [],
    });

    useEffect(() => {
        fetchGroupedInvoices();
    }, [pagination.current, pagination.pageSize, filters]);

    const fetchGroupedInvoices = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.current,
                limit: pagination.pageSize,
                search: filters.search,
                status: filters.status,
                startDate: filters.dateRange?.[0]?.toISOString(),
                endDate: filters.dateRange?.[1]?.toISOString(),
            };
            // Используем новый эндпоинт
            const response = await axios.get('/api/invoices', { params });
            setGroupedData(response.data.data);
            setPagination(prev => ({ ...prev, total: response.data.total }));
        } catch (error) {
            message.error('Ошибка при загрузке ведомости');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({ ...prev, [name]: value }));
        setPagination(prev => ({ ...prev, current: 1 })); // Сброс на 1 страницу при фильтрации
    };

    // --- Колонки для вложенной таблицы (счета) ---
    const nestedColumns = [
        { title: 'Контрагент', dataIndex: 'counterparty', key: 'counterparty' },
        { title: 'Статья', dataIndex: 'article', key: 'article' },
        { title: 'Наименование', dataIndex: 'rate_name', key: 'rate_name' },
        { title: 'Кол-во', dataIndex: 'quantity', key: 'quantity', align: 'right' },
        { title: 'Цена', dataIndex: 'price', key: 'price', align: 'right', render: (val) => parseFloat(val).toLocaleString('ru-RU') },
        { title: 'Сумма', dataIndex: 'amount', key: 'amount', align: 'right', render: (val) => parseFloat(val).toLocaleString('ru-RU', { style: 'currency', currency: 'RUB' }) },
        { title: 'Примечание', dataIndex: 'note', key: 'note' },
    ];

    // --- Рендер раскрывающейся строки ---
    const expandedRowRender = (record) => {
        const totalIncome = record.income.reduce((sum, item) => sum + parseFloat(item.amount), 0);
        const totalExpense = record.expense.reduce((sum, item) => sum + parseFloat(item.amount), 0);
        const profit = totalIncome - totalExpense;

        return (
            <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                <Title level={5}>Доходы</Title>
                <Table
                    columns={nestedColumns}
                    dataSource={record.income}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    summary={() => (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={5} align="right"><Text strong>Итого доходов:</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                                <Text strong style={{ color: 'green' }}>{totalIncome.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB' })}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                        </Table.Summary.Row>
                    )}
                />

                <Title level={5} style={{ marginTop: '16px' }}>Расходы</Title>
                <Table
                    columns={nestedColumns}
                    dataSource={record.expense}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    summary={() => (
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={5} align="right"><Text strong>Итого расходов:</Text></Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="right">
                                <Text strong style={{ color: 'red' }}>{totalExpense.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB' })}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2}></Table.Summary.Cell>
                        </Table.Summary.Row>
                    )}
                />

                <div style={{ textAlign: 'right', marginTop: '16px' }}>
                    <Title level={4}>
                        Прибыль по задаче: <Text style={{ color: profit >= 0 ? 'green' : 'red' }}>{profit.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB' })}</Text>
                    </Title>
                </div>
            </div>
        );
    };

    // --- Колонки для основной таблицы (задачи) ---
    const mainColumns = [
        { title: 'ID Задачи', dataIndex: 'id', key: 'id' },
        { title: 'Внешний ID', dataIndex: 'external_id', key: 'external_id' },
        { title: 'Название', dataIndex: 'title', key: 'title' },
        {
            title: 'Статус ВДР',
            dataIndex: 'status_invoices',
            key: 'status_invoices',
            render: (status) => {
                const statusInfo = TASK_STATUS_OPTIONS.find(s => s.value === status);
                return <Tag>{statusInfo ? statusInfo.label : status}</Tag>;
            },
        },
        { title: 'Дата создания', dataIndex: 'created_at', key: 'created_at', render: (date) => new Date(date).toLocaleDateString('ru-RU') },
    ];

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Title level={2}>Ведомость доходов и расходов</Title>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col>
                        <Input.Search
                            placeholder="Поиск по ID или названию задачи"
                            allowClear
                            style={{ width: 300 }}
                            onSearch={(value) => handleFilterChange('search', value)}
                            enterButton
                        />
                    </Col>
                    <Col>
                        <Select
                            placeholder="Фильтр по статусу ВДР"
                            allowClear
                            style={{ width: 220 }}
                            onChange={(value) => handleFilterChange('status', value)}
                        >
                            {TASK_STATUS_OPTIONS.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)}
                        </Select>
                    </Col>
                    <Col>
                        <RangePicker
                            onChange={(dates) => handleFilterChange('dateRange', dates)}
                        />
                    </Col>
                </Row>

                <Row justify="end" style={{ marginBottom: 16 }}>
                    <Space>
                        <Button icon={<DownloadOutlined />}>Экспорт</Button>
                        <Button icon={<UploadOutlined />}>Импорт</Button>
                    </Space>
                </Row>

                <Table
                    columns={mainColumns}
                    dataSource={groupedData}
                    rowKey="id"
                    loading={loading}
                    pagination={pagination}
                    onChange={(p) => setPagination(p)}
                    expandable={{ expandedRowRender }}
                    bordered
                />
            </div>
        </Layout>
    );
};

export default InvoicesPage;